import type { SearchResultFragment, WishlistProductsQuery } from '#graphql-operations'
import type { ComputedRef, Ref } from 'vue'
import type { LookCardData } from '~/composables/useLooks'
import { ToastAction } from '#components'
import { SortOrder } from '#graphql-operations'
import { chunkIds } from '~/utils/listQuery'
import { sortBySize, variantSizeLabel } from '~/utils/size'
import { SIZE_PROFILE_GROUPS } from '~/utils/sizeProfileCatalog'
import { placeOf, restorePlaces } from '~/utils/wishlistPlace'

/**
 * One list of renderable wishlist rows, whoever the visitor is.
 *
 * Both a customer's saves (off the account) and a guest's (ids in their
 * browser) are read against the catalogue through `wishlistProducts`, which is
 * what gives every row its colour, its garment type and the sizes the product
 * is made in. Ids are all a guest stores, so a price shown here is always
 * today's price rather than whatever it was when they hearted it.
 */

export type WishlistProduct = WishlistProductsQuery['products']['items'][number]

/** What kind of garment a saved piece is — the account's filter chips. */
export type WishlistGroup = 'tops' | 'bottoms' | 'accessories'
export const WISHLIST_GROUPS: readonly WishlistGroup[] = ['tops', 'bottoms', 'accessories']

export interface WishlistSizeOption {
  /** "M" */
  label: string
  variantId: string
  available: boolean
}

/** Whether a size can still be chosen: one of the product's, and not sold out. */
export function isSizeOffered(sizes: WishlistSizeOption[], variantId: string | undefined): boolean {
  return sizes.some(size => size.variantId === variantId && size.available)
}

/** The product's reviews: `stars` a raw mean (shown to one decimal), `count` never 0. */
export interface WishlistRating {
  stars: number
  count: number
}

export interface WishlistRow {
  key: string
  variantId: string
  /** For putting the row back after a removal: a guest's save needs it. */
  productId: string
  image?: string
  /**
   * The product's name. The saved variant's own name ends in a size the
   * shopper may never have chosen — a tile's heart saves whichever variant the
   * search index returned first — so it is not what a card or a removal toast shows.
   */
  name: string
  price: string
  productHref: string
  /** The colour the product is in ("Black"), from its `color` facet. */
  colour?: string
  /**
   * Undefined until the catalogue has answered, for a product it no longer carries, and for one
   * whose category no chip covers (see `wishlistGroup`).
   */
  group?: WishlistGroup
  /** Every size the product is made in, in size order; empty until the catalogue has answered. */
  sizes: WishlistSizeOption[]
  /** The size the heart saved ("S"), when the variant has one. */
  savedSize?: string
  /** No size left to buy. Only ever true once the catalogue has answered. */
  soldOut: boolean
  /** `null` for a product nobody has reviewed; undefined until the catalogue has answered, and for one it no longer carries. */
  rating?: WishlistRating | null
  /**
   * An account row the catalogue has not described: not read yet (or the read failed), or `gone` —
   * disabled, deleted or out of this channel. Undefined once it has. Tells "no sizes to offer" from
   * "nothing to buy", which both leave `sizes` empty.
   */
  catalogue?: 'unread' | 'gone'
}

/**
 * The ids that tie a wishlist tab (`WishlistTabs`) to the panel it shows, so assistive tech can
 * name the panel by its tab and go from one to the other. `prefix` is one `useId()` per page.
 */
export function wishlistTabIds(prefix: string, value: string): { tab: string, panel: string } {
  return { tab: `${prefix}-tab-${value}`, panel: `${prefix}-panel-${value}` }
}

/** A product card as the account's grids show it: `AccountProductCard`'s props, keyed. */
export interface WishlistCardItem {
  key: string
  name: string
  href?: string
  image?: string | null
  subtitle?: string
  price?: string
  oldPrice?: string
  saleBadge?: string
  /** The sale's percent, for the badge's short "-44%" on a phone (`AccountProductCard`'s `shop`). */
  savedPercent?: number
  productId?: string
  variantId?: string
  soldOut?: boolean
  /** The sizes the product is made in, for a card that can go to the cart (`WishlistProductGrid`'s `cart`). */
  sizes?: WishlistSizeOption[]
  /** As on `WishlistRow`: `null` draws "no reviews yet", undefined no line at all. */
  rating?: WishlistRating | null
  /** As on `WishlistRow`. */
  catalogue?: 'unread' | 'gone'
  /** The size the card starts on, for one that names its saved size. */
  presetVariantId?: string
}

export function productHref(id: string | number, slug?: string | null): string {
  return slug ? `/products/${id}/${slug}` : `/products/${id}`
}

const GROUP_BY_CATEGORY = new Map<string, WishlistGroup>(
  SIZE_PROFILE_GROUPS.flatMap(group => group.categories.map(category => [category.code, group.key] as const)),
)

/**
 * The `category` values that are accessories: the header menu's "Doplňky" column (caps, socks,
 * hair accessories, yoga mats, gift vouchers) and the bag. Named rather than inferred from
 * "not a top or a bottom": the facet also holds a product line (`exld`) and garments no chip
 * covers (`dress`), and two live sports bras carry no category at all — none of them is an
 * accessory.
 */
const ACCESSORY_CATEGORIES = new Set(['caps', 'socks', 'hair-accessories', 'yoga-mat', 'gift-card', 'bag'])

/**
 * Which chip a product files under, by its `category` facet — the tops/bottoms
 * split the wardrobe picker walks (`SIZE_PROFILE_GROUPS`), so the two surfaces
 * cannot disagree about what a top is. A product may carry a product line
 * beside its garment type (`exld` + `leggings`), so the first value that names
 * a top or a bottom decides, then one that names an accessory. Anything else —
 * no category, or one no chip covers — files under no chip: it is listed under
 * "Vše" only, rather than under a chip that would misname it.
 */
export function wishlistGroup(facetValues: readonly { code: string, facet: { code: string } }[]): WishlistGroup | undefined {
  const categories = facetValues.filter(value => value.facet.code === 'category').map(value => value.code)
  for (const code of categories) {
    const group = GROUP_BY_CATEGORY.get(code)
    if (group)
      return group
  }
  return categories.some(code => ACCESSORY_CATEGORIES.has(code)) ? 'accessories' : undefined
}

/** The product's own value of a facet ("Emerald Shade" for `color`). */
export function facetName(product: WishlistProduct, facetCode: string): string | undefined {
  return product.facetValues.find(value => value.facet.code === facetCode)?.name
}

// As the upsell rails read it: no count means no reviews, whatever the mean says.
export function productRating(product: WishlistProduct): WishlistRating | null {
  const count = product.customFields?.reviewCount
  return count ? { stars: product.customFields?.reviewRating ?? 0, count } : null
}

export function sizeOptions(product: WishlistProduct): WishlistSizeOption[] {
  return sortBySize(
    product.variants.map(variant => ({
      label: variantSizeLabel(product.name, variant.name),
      variantId: String(variant.id),
      available: variant.stockLevel !== 'OUT_OF_STOCK',
    })),
    option => option.label,
  )
}

/** The size in a variant's name, or undefined when the name carries none. */
function sizeOf(productName: string, variantName: string): string | undefined {
  const label = variantSizeLabel(productName, variantName)
  return label === variantName.trim() ? undefined : label
}

/**
 * The products behind the wishlist, read from the catalogue once each and kept
 * for the life of the page: the rows read them, and so do the suggestions under
 * the account's list, which is what lets a suggestion hearted into the list
 * arrive there with its colour and chip already known. `null` records a product
 * the catalogue did not return — disabled, deleted, or moved out of this
 * channel — so it is not asked about again.
 */
function useWishlistProductCache() {
  const products = shallowRef<Record<string, WishlistProduct | null>>({})

  /**
   * Reads the ids and writes the answers over what is kept, in one step and only once they are in —
   * an entry is never emptied while it is re-read, so a card never leaves the grid for it.
   */
  async function read(ids: readonly string[]): Promise<void> {
    // Batched: a long list would otherwise cross Vendure's `take` ceiling or
    // the WAF's query-string limit and fail as a whole (see `chunkIds`). Each
    // batch stands alone, so one that fails does not keep the others' rows off
    // the page; the read still fails afterwards, for the ids left unanswered.
    const results = await Promise.allSettled(chunkIds([...ids]).map(async (batch) => {
      const { data, errors } = await useGraphqlQuery('wishlistProducts', { ids: batch, take: batch.length })
      // A single product with a null translated name fails the whole read
      // (Product.name is non-nullable). An answer without the list is a
      // failure, never "none of these exist" — reading it as the latter would
      // prune a guest's saves.
      if (!data?.products)
        throw new Error(errors?.[0]?.message ?? 'wishlistProducts returned no products')
      return { batch, items: data.products.items as WishlistProduct[] }
    }))

    const answered = results.flatMap(result => result.status === 'fulfilled' ? [result.value] : [])
    if (answered.length) {
      const next = { ...products.value }
      for (const { batch, items } of answered) {
        for (const id of batch)
          next[id] = null
        for (const product of items)
          next[String(product.id)] = product
      }
      products.value = next
    }

    const failed = results.find(result => result.status === 'rejected')
    if (failed)
      throw failed.reason
  }

  async function load(ids: readonly string[]): Promise<void> {
    const missing = [...new Set(ids)].filter(id => !(id in products.value))
    if (missing.length)
      await read(missing)
  }

  /**
   * Reads products again that were read before — the stock on a card is otherwise the page's first
   * read for as long as it stays open. A failure keeps what was known.
   */
  async function reload(ids: readonly string[]): Promise<void> {
    try {
      await read([...new Set(ids)])
    }
    catch (error) {
      console.error('Failed to re-read the wishlist products', error)
    }
  }

  return { products, load, reload }
}

export function useWishlistRows() {
  const { items, guestEntries, guestWishlistLoaded, keepGuestWishlistEntries, loading } = useObserved()
  const priceFormatter = usePriceFormatter()
  const { products, load, reload } = useWishlistProductCache()

  const productIds = computed(() => [...new Set([
    ...items.value.map(item => String(item.productVariant.product.id)),
    ...guestEntries.value.map(entry => entry.productId),
  ])])

  // Distinguishes "the catalogue says these are gone" from "the request failed":
  // the first prunes storage, the second must never be read as an empty wishlist.
  const hydrationFailed = ref(false)

  // Client-only, so nothing about this visitor's saves reaches server-rendered
  // HTML. The key follows the ids, but only products not read yet are fetched,
  // so a removal (or its undo) costs no request. No `default`: Nuxt skips a
  // lazy fetch whose data ref is already defined, and the ids only arrive after
  // the lists load post-mount.
  //
  // The key carries this caller's own id because the handler fills this
  // caller's own cache. Nuxt hands a second caller of a live key the FIRST
  // caller's handler — and `/wishlist` is still mounted while it redirects to
  // `/account/wishlist`, with the very same ids — so a shared key would fill the
  // leaving page's cache and leave the arriving one loading for good.
  const instance = useId()
  const { refresh } = useLazyAsyncData(
    () => `wishlist-products:${instance}:${productIds.value.join(',')}`,
    async () => {
      try {
        await load(productIds.value)
        hydrationFailed.value = false
      }
      catch (error) {
        // Recoverable — the saved ids are untouched — so a guest's page stays
        // loading rather than telling them their wishlist is empty when it is not.
        console.error('Failed to load the wishlist products', error)
        hydrationFailed.value = true
      }
      return true
    },
    { server: false },
  )

  const resolvedVariants = computed(() => {
    const byVariantId = new Map<string, { product: WishlistProduct, variant: WishlistProduct['variants'][number] }>()

    for (const product of Object.values(products.value)) {
      if (!product)
        continue
      for (const variant of product.variants)
        byVariantId.set(String(variant.id), { product, variant })
    }

    return byVariantId
  })

  function catalogueFields(product: WishlistProduct) {
    const sizes = sizeOptions(product)
    return {
      colour: facetName(product, 'color'),
      group: wishlistGroup(product.facetValues),
      sizes,
      soldOut: sizes.length > 0 && sizes.every(size => !size.available),
      rating: productRating(product),
    }
  }

  // Rendered from the account's own item when the catalogue no longer returns
  // the product, as before — the customer saved it, so it stays listed until
  // they remove it; it just has no sizes to offer.
  const accountRows = computed<WishlistRow[]>(() => items.value.map((item) => {
    const variantId = String(item.productVariant.id)
    const productId = String(item.productVariant.product.id)
    const resolved = resolvedVariants.value.get(variantId)
    const product = resolved?.product ?? products.value[productId] ?? undefined
    const name = product?.name ?? item.productVariant.product.name
    const catalogue = product ? undefined : productId in products.value ? 'gone' as const : 'unread' as const

    return {
      key: `account:${item.id}`,
      variantId,
      productId,
      image: item.productVariant.featuredAsset?.preview ?? item.productVariant.product.featuredAsset?.preview,
      name,
      price: priceFormatter(resolved?.variant.price ?? item.productVariant.price, resolved?.variant.currency ?? item.productVariant.currency),
      productHref: productHref(productId, item.productVariant.product.slug),
      savedSize: sizeOf(item.productVariant.product.name, item.productVariant.name),
      ...(product ? catalogueFields(product) : { sizes: [], soldOut: false, catalogue }),
    }
  }))

  const guestRows = computed<WishlistRow[]>(() => {
    const rows: WishlistRow[] = []

    for (const entry of guestEntries.value) {
      const resolved = resolvedVariants.value.get(entry.variantId)
      if (!resolved)
        continue

      const { product, variant } = resolved

      rows.push({
        key: `guest:${entry.variantId}`,
        variantId: entry.variantId,
        productId: String(product.id),
        image: variant.featuredAsset?.preview ?? product.featuredAsset?.preview,
        name: product.name,
        price: priceFormatter(variant.price, variant.currency),
        productHref: productHref(product.id, product.slug),
        savedSize: sizeOf(product.name, variant.name),
        ...catalogueFields(product),
      })
    }

    return rows
  })

  // Once the catalogue has answered for every product a guest saved, anything
  // it did not return is genuinely gone — disabled, deleted, or moved out of
  // this channel. Drop it, so the page and the header badge cannot disagree
  // about how many items are saved. (The cache only ever changes on a read that
  // succeeded, so an answered product here is a real answer.)
  watch(products, () => {
    if (!guestEntries.value.length || guestEntries.value.some(entry => !(entry.productId in products.value)))
      return

    keepGuestWishlistEntries(
      guestEntries.value
        .filter(entry => resolvedVariants.value.has(entry.variantId))
        .map(entry => entry.variantId),
    )
  })

  // A piece put back with Undo keeps its old place for as long as the page is
  // open (`restorePlaces`), rather than coming back as a new save would.
  const placedAfter = shallowRef(new Map<string, string | null>())

  // Concatenated and de-duplicated rather than switched on `loggedIn`: for the
  // moment between signing in and the merge finishing both lists are populated,
  // and this way that moment shows each item once instead of twice.
  const rows = computed<WishlistRow[]>(() => {
    const seen = new Set<string>()
    const merged: WishlistRow[] = []

    for (const row of [...accountRows.value, ...guestRows.value]) {
      if (seen.has(row.variantId))
        continue

      seen.add(row.variantId)
      merged.push(row)
    }

    return restorePlaces(merged, row => row.variantId, placedAfter.value)
  })

  /**
   * Notes where a piece sits as it is taken off, and returns what puts it back there — for the
   * Undo to call, so a piece saved again any other way still arrives first.
   */
  function rememberPlace(variantId: string): () => void {
    const after = placeOf(rows.value, row => row.variantId, variantId) ?? null
    const next = new Map(placedAfter.value)
    next.delete(variantId)
    placedAfter.value = next
    return () => {
      placedAfter.value = new Map(placedAfter.value).set(variantId, after)
    }
  }

  // Stays true on the server and through hydration, so the page renders one
  // stable shape for everyone and only then reveals whatever this visitor saved.
  // Also waits for the catalogue, so the account's chips and colours arrive with
  // the cards rather than after them. A failed read ends the wait for the
  // account's rows, which can still be drawn from the account itself, but not
  // for a guest's, which cannot — a recoverable error never renders as "your
  // wishlist is empty".
  const isLoading = computed(() => {
    if (!guestWishlistLoaded.value || loading.value)
      return true
    const unread = productIds.value.filter(id => !(id in products.value))
    if (!unread.length)
      return false
    return !hydrationFailed.value || guestEntries.value.some(entry => unread.includes(entry.productId))
  })

  // The read failed and left products unread: a guest's rows cannot be drawn
  // at all then, and an account's have no sizes. Says so, with a way to try again.
  const loadFailed = computed(() => hydrationFailed.value && productIds.value.some(id => !(id in products.value)))

  return {
    rows,
    isLoading,
    loadFailed,
    // The key is unchanged, so this re-runs the read, which asks only for what is still unread.
    retry: () => refresh(),
    products,
    loadProducts: load,
    reloadProducts: reload,
    rememberPlace,
  }
}

/**
 * Every removal from the wishlist is confirmed with a way back: the dark toast
 * names what went and offers "Undo", which puts it straight back through the
 * same store call the heart uses. No confirmation dialog before — a wishlist is
 * a list of maybes, and a second chance after is cheaper for the shopper than a
 * question before. Shared by `/wishlist` and the account's list.
 *
 * Only one toast shows at a time, so a second removal while the first's Undo is
 * still up joins it: the new toast names both and its Undo puts both back.
 */
export interface WishlistRemovalHooks {
  /** Called when Undo is pressed, before the piece is put back (`rememberPlace`). */
  onUndo?: () => void
  /** Called once it is back; `viaKeyboard` when Undo was pressed from the keyboard. */
  onRestored?: (viaKeyboard: boolean) => void
}

interface PendingRemoval extends WishlistRemovalHooks {
  name: string
  restore: () => Promise<void>
}

export function useWishlistRemoval() {
  const { addToWishlist, removeFromWishlist, saveLook, unsaveLook } = useObserved()
  const { toast, toasts } = useToast()
  const { t, locale } = useI18n()

  let undoToastId: string | null = null
  let pending: PendingRemoval[] = []

  function confirmRemoval(name: string, restore: () => Promise<void>, hooks: WishlistRemovalHooks = {}) {
    // The earlier Undo is gone — timed out, dismissed, or replaced by another toast — and with it the offer.
    if (!toasts.value.some(item => item.id === undoToastId && item.open !== false))
      pending = []
    pending = [...pending, { name, restore, ...hooks }]
    const batch = pending

    // A fresh toast rather than an update of the open one, so its countdown starts again.
    undoToastId = toast({
      variant: 'success',
      // Its own place: bottom centre, under whatever the shopper is looking at,
      // rather than the corner the other toasts use — an undo has to be where
      // the eye already is. `fixed` lifts it out of the viewport's stack; the
      // horizontal insets with `w-max` + `mx-auto` centre it without a
      // transform, which the toast's own slide animations would fight over.
      // The sentence goes in as the description: the title slot is bold, and a
      // confirmation is not a headline.
      class: 'fixed inset-x-4 bottom-6 mx-auto w-max max-w-[calc(100vw-2rem)] items-center gap-4 rounded-lg px-6 py-4 sm:inset-x-0',
      description: t('wishlist.removed.toast', {
        name: new Intl.ListFormat(locale.value, { type: 'conjunction' }).format(batch.map(removal => removal.name)),
      }),
      action: h(ToastAction, {
        altText: t('wishlist.removed.undo'),
        class: 'h-auto border-0 bg-transparent px-0 text-[15px] text-white underline underline-offset-[3px] hover:bg-transparent hover:opacity-80',
        onClick: async (event: MouseEvent) => {
          pending = []
          undoToastId = null
          // A keyboard press is a click with no pointer detail.
          const viaKeyboard = event.detail === 0
          // One at a time: each reply is the whole list, and the store sends them in order.
          for (const removal of batch) {
            removal.onUndo?.()
            try {
              await removal.restore()
            }
            catch (error) {
              console.error('Failed to restore the wishlist item', error)
              toast({ variant: 'destructive', title: t('account.wishlist.restore_error') })
              return
            }
            removal.onRestored?.(viaKeyboard)
          }
        },
      }, () => t('wishlist.removed.undo')),
    }).id
  }

  // A removal the account refused (an expired session, say) leaves the piece
  // listed — the store keeps what it shows — and says so, instead of offering
  // to undo what never happened. A guest's removal is local and cannot fail.
  function reportRemovalError(error: unknown) {
    console.error('Failed to remove from the wishlist', error)
    toast({ variant: 'destructive', title: t('account.wishlist.remove_error') })
  }

  async function removeItem(row: WishlistRow, hooks?: WishlistRemovalHooks) {
    try {
      await removeFromWishlist(row.variantId)
    }
    catch (error) {
      reportRemovalError(error)
      return
    }
    confirmRemoval(row.name, () => addToWishlist(row.variantId, row.productId), hooks)
  }

  async function removeLook(look: LookCardData, hooks?: WishlistRemovalHooks) {
    const id = String(look.id)
    try {
      await unsaveLook(id)
    }
    catch (error) {
      reportRemovalError(error)
      return
    }
    confirmRemoval(look.title || t('looks.shop_the_look.title'), () => saveLook(id), hooks)
  }

  return { confirmRemoval, removeItem, removeLook }
}

export type WishlistAddOutcome = 'added' | 'out-of-stock' | 'failed'

/**
 * Adds a saved piece to the cart in the size the shopper picked on its card —
 * which is a variant of its own, not necessarily the one the heart saved (a
 * tile's heart saves whichever variant the search index returned first).
 * `addingKey` names the card whose add is on its way. It is one for the whole
 * app, so while it is set no other card's add is sent: two at once would race
 * over the one cart drawer (the first to settle would drop its skeleton line,
 * or close it under the other). Cards show that they are waiting (`busy`).
 */
export function useWishlistAddToCart() {
  const activeOrderStore = useActiveOrderStore()
  const { addVariantToOrder, reportAddToCartError } = useCartAdd()
  const drawers = useDrawers()
  const { ecommerce } = useAnalytics()
  const tiktokPixel = useTikTokPixel()
  const pinterestPixel = usePinterestPixel()
  const addingKey = useState<string | null>('wishlist-cart-adding', () => null)

  // The same add_to_cart events the product page and quick add send, built from
  // the order line the add just returned: the card holds no price of its own.
  function trackAdd(variantId: string) {
    const order = activeOrderStore.activeOrder.data
    // Not a gift line of the same variant: that one is priced 0.
    const line = order?.lines.find(candidate => String(candidate.productVariant.id) === String(variantId) && !isGiftOrderLine(candidate))
    if (!order || !line)
      return
    // A tracker that throws must never turn an add that landed into a failed one.
    try {
      ecommerce.addToCart({
        id: line.productVariant.id,
        name: line.productVariant.name,
        price: line.unitPrice,
        currency: order.currency,
        options: line.productVariant.options,
        productId: line.productVariant.product.id,
      } as unknown as Parameters<typeof ecommerce.addToCart>[0], 1)
      tiktokPixel.addToCart({ id: String(variantId), price: line.unitPrice, quantity: 1 }, order.currency)
      pinterestPixel.addToCart({ id: String(variantId), price: line.unitPrice, quantity: 1 }, order.currency)
    }
    catch (error) {
      console.error('Failed to report add_to_cart', error)
    }
  }

  /** Undefined when another add was still on its way and this one was not sent. */
  async function addToCart(key: string, variantId: string): Promise<WishlistAddOutcome | undefined> {
    if (addingKey.value)
      return
    addingKey.value = key
    // Open the drawer optimistically — it shows a skeleton line (`cart.adding`)
    // while the mutation is in flight.
    const drawerWasOpen = drawers.value.cart.open
    drawers.value.cart.open = true
    drawers.value.cart.adding = true
    try {
      await addVariantToOrder(variantId, 1)
      trackAdd(variantId)
      return 'added'
    }
    catch (error) {
      // Closed again only when this add opened it (the scrim covers the page
      // while it is open, so in practice it always did).
      if (!drawerWasOpen)
        drawers.value.cart.open = false
      return reportAddToCartError(error)
    }
    finally {
      addingKey.value = null
      drawers.value.cart.adding = false
    }
  }

  // `trackAdd` too, for an add made elsewhere through the same cart path (a shared list's
  // "Přidat vše do košíku" adds piece by piece and reports each as this card would).
  return { addingKey, addToCart, trackAdd }
}

/** Cards under the account's list: "Ještě něco do vašeho výběru?", or the empty state's first ideas. */
const SUGGESTION_COUNT = 4
// The most recently saved products the suggestions are built around. Each one
// is a similarity search of its own, so this is also the request count.
const SEED_COUNT = 2
// Grouped by product, so every colour of a design is a hit of its own and most
// of a product's neighbours are the other colours of the pieces it resembles.
// The name dedupe below folds those, so ask for enough to leave several designs.
const SIMILAR_TAKE = 12
const BESTSELLER_TAKE = 16
// Candidates read in full (colour, stock) before choosing: enough that the
// ones sold out, already saved, or hearted from the row itself leave it full.
const CANDIDATE_COUNT = 10

function designName(name?: string | null): string {
  return (name ?? '').trim().toLowerCase()
}

/**
 * Products to add to the wishlist, for the account's list: the ones most like
 * what the customer saved last (`similarProductId`, the index's embedding
 * search), topped up with the best sellers when that finds too few — or finds
 * nothing, as it does wherever the index holds no embeddings. With nothing
 * saved it is the best sellers alone.
 *
 * Built once per visit from the list as it first loads, so hearting a card
 * here does not reshuffle the row: the hearted piece moves up into the list
 * (it is filtered out below as soon as it is saved) and the next candidate
 * takes its place. One design per row — the other colours of a saved piece,
 * or of a suggestion, are not new ideas — and nothing sold out.
 */
export function useWishlistInspiration(options: {
  rows: ComputedRef<WishlistRow[]>
  /** True once the list has loaded, with its products read. */
  ready: Ref<boolean>
  products: Ref<Record<string, WishlistProduct | null>>
  loadProducts: (ids: readonly string[]) => Promise<void>
}) {
  const { rows, ready, products, loadProducts } = options
  const priceFormatter = usePriceFormatter()

  const seeds = ref<{ ids: string[], genders: string[] } | null>(null)

  watch(ready, (isReady) => {
    if (!isReady || seeds.value)
      return

    const ids = [...new Set(rows.value.map(row => row.productId))]
    // A list that is all women's (or all men's) gets best sellers for that
    // gender — unisex pieces count for both, as in the wardrobe picker.
    const genders = new Set<string>()
    for (const id of ids) {
      for (const value of products.value[id]?.facetValues ?? []) {
        if (value.facet.code === 'gender' && value.code !== 'unisex')
          genders.add(value.code)
      }
    }

    seeds.value = {
      ids: ids.slice(0, SEED_COUNT),
      genders: genders.size === 1 ? [...genders, 'unisex'] : [],
    }
  }, { immediate: true })

  // Client-only; `search` is not personalised, so the index's answers may come
  // from the edge cache. Nothing about the customer goes into its variables
  // beyond the product ids asked about — the list itself is filtered here.
  // Keyed per caller, like the rows' read: the handler works on this caller's list.
  const instance = useId()
  const { data: hits, status } = useLazyAsyncData(
    () => `wishlist-inspiration:${instance}:${seeds.value ? `${seeds.value.ids.join(',')}|${seeds.value.genders.join(',')}` : 'waiting'}`,
    async (): Promise<SearchResultFragment[]> => {
      const seed = seeds.value
      if (!seed)
        return []

      const lists = await Promise.all(seed.ids.map(id =>
        useGraphqlQuery('search', { input: { similarProductId: id, groupByProduct: true, take: SIMILAR_TAKE } })
          .then(result => (result.data?.search?.items ?? []) as SearchResultFragment[])
          .catch(() => [] as SearchResultFragment[]),
      ))

      // Taken in turns, so each saved piece gets its nearest neighbours in
      // before either gets its tenth.
      const pool: SearchResultFragment[] = []
      for (let index = 0; index < SIMILAR_TAKE; index++) {
        for (const list of lists) {
          const hit = list[index]
          if (hit)
            pool.push(hit)
        }
      }

      const excluded = new Set([...seed.ids, ...rows.value.map(row => row.productId)])
      const excludedNames = new Set(rows.value.map(row => designName(row.name)))
      const candidates: SearchResultFragment[] = []
      const names = new Set<string>()
      const take = (list: SearchResultFragment[]) => {
        for (const hit of list) {
          if (candidates.length === CANDIDATE_COUNT)
            return
          const name = designName(hit.name)
          if (excluded.has(String(hit.id)) || excludedNames.has(name) || names.has(name))
            continue
          names.add(name)
          candidates.push(hit)
        }
      }
      take(pool)

      if (candidates.length < CANDIDATE_COUNT) {
        // Ordered by `sales`, as `useBestsellerHighlights` explains: the
        // rolling `bestSelling` key has no data in the production index.
        const bestsellers = await useGraphqlQuery('search', {
          input: {
            groupByProduct: true,
            take: BESTSELLER_TAKE,
            sort: { sales: SortOrder.DESC },
            ...(seed.genders.length
              ? { facetValueCodeFilters: [{ facetCode: 'gender', valueCodes: seed.genders, byProduct: true }] }
              : {}),
          },
        })
          .then(result => (result.data?.search?.items ?? []) as SearchResultFragment[])
          .catch(() => [] as SearchResultFragment[])
        take(bestsellers)
      }

      // Their colour, and whether anything is left to buy. A failed read only
      // costs the colour line; stock is then taken on trust.
      await loadProducts(candidates.map(hit => String(hit.id))).catch(() => {})
      return candidates
    },
    { server: false },
  )

  const items = computed<WishlistCardItem[]>(() => {
    const saved = new Set(rows.value.map(row => row.productId))
    const savedNames = new Set(rows.value.map(row => designName(row.name)))
    const cards: WishlistCardItem[] = []

    for (const hit of hits.value ?? []) {
      const id = String(hit.id)
      const product = products.value[id] ?? undefined
      if (saved.has(id) || savedNames.has(designName(hit.name)))
        continue
      if (product?.variants.length && product.variants.every(variant => variant.stockLevel === 'OUT_OF_STOCK'))
        continue

      const price = searchResultPrice(hit.price)
      cards.push({
        key: `suggestion:${id}`,
        name: product?.name ?? hit.name,
        href: productHref(id, hit.slug),
        image: hit.featuredProductAsset?.preview ?? product?.featuredAsset?.preview,
        subtitle: product ? facetName(product, 'color') : undefined,
        price: price === undefined ? undefined : priceFormatter(price, hit.currency),
        productId: id,
        variantId: hit.productVariantId ? String(hit.productVariantId) : undefined,
        rating: product ? productRating(product) : undefined,
      })

      if (cards.length === SUGGESTION_COUNT)
        break
    }

    return cards
  })

  const pending = computed(() => !seeds.value || status.value === 'idle' || status.value === 'pending')

  return { items, pending }
}
